import React from 'react';
import { useRouter } from 'next/router';
import { t, addTranslationKeys } from 'Src/i18n';
import * as routeBuilder from 'Routes/routeBuilder';
import { redirectToOidc } from 'Utils/uri/redirectUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { Button } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Text } from 'Components/frameworks/text/Text';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import { TRACK_EVENT_NAMES, TRACK_PAGE_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
export type SubmissionStatusProps = {
  id?: string;
  text: string;
  title: string;
  titleDate: string;
  textCustomerAccount: string;
  link: {
    labe: string;
    url: string;
  };
  consultRequest: string;
  anotherRequestLink: {
    label: string;
    url: string;
  };
  travelSaved: string;
};
export const SubmissionStatusLine = ({
  id,
  text,
  title,
  titleDate,
  link,
  textCustomerAccount,
  anotherRequestLink,
  consultRequest,
  travelSaved,
}: SubmissionStatusProps) => {
  const router = useRouter();
  const appContext = useAppContext();
  const { isUserLogged } = appContext;
  const { proposalId } = router.query;
  const accountSearchUrl = routeBuilder.buildUrl('accountSearches');
  const prebookingformUrl = anotherRequestLink?.url;
  addTranslationKeys({
    submissionStatus: {
      text,
      title,
      titleDate,
      textCustomerAccount,
      link,
      anotherRequestLink,
      consultRequest,
      travelSaved,
    },
  });
  const { trackClickEvent } = useTrackEvent();
  return (
    <SiteContainer className="Line Line-SubmissionStatus px-20 py-40" id={id} size="small">
      {proposalId && (
        <TrackPage
          pageName={TRACK_PAGE_NAMES.PREBOOKING_FORM_CONFIRMATION}
          payload={{
            reference_id: proposalId || '',
          }}
        />
      )}
      <div className="w-full">
        <div className="flex w-full justify-center">
          <Icon className="shrink-0 text-green" name={ICONS.CHECK_OUTLINE} width="50px" />
        </div>
        <div className="mt-12 text-center font-brand text-40 font-bold">
          <p>{t('submissionStatus.title')}</p>
          <p>{titleDate ? t('submissionStatus.titleDate') : ''}</p>
        </div>
        {proposalId && (
          <p className="mt-12 text-center text-16 font-semibold">
            {travelSaved
              ? t('submissionStatus.travelSaved', {
                  proposalId,
                })
              : ''}
          </p>
        )}
        <div className="mt-40 text-center text-16">
          <Text text={t('submissionStatus.text')} />
          {proposalId && <Text text={t('submissionStatus.textCustomerAccount')} />}
        </div>
        <div className="mt-40 grid grid-cols-1 gap-20 sm:flex sm:items-center sm:justify-center">
          {proposalId && (
            <Button
              colorBg={COLORS.TRANSPARENT}
              colorBorder={COLORS.BLACK}
              data-testid="SubmissionStatusLine-consultRequest"
              onClick={() => {
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FORM_PREREG, {
                  detail_click: 'check_my_request',
                });

                if (isUserLogged) {
                  return router.push(accountSearchUrl);
                }

                return redirectToOidc(appContext, accountSearchUrl);
              }}
            >
              {t('submissionStatus.consultRequest')}
            </Button>
          )}
          {anotherRequestLink ? (
            <Button
              data-testid="SubmissionStatusLine-newRequest"
              onClick={() => {
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_PREBOOKING_FORM_PREREG, {
                  detail_click: 'make_new_request',
                });
                router.push(prebookingformUrl);
              }}
            >
              {t('submissionStatus.anotherRequestLink.label')}
            </Button>
          ) : (
            <Button
              data-testid="SubmissionStatusLine-ok"
              onClick={() => {
                router.push(link?.url);
              }}
            >
              {t('submissionStatus.link.label')}
            </Button>
          )}
        </div>
      </div>
    </SiteContainer>
  );
};
